import { useCallback, useEffect, useRef, useState } from "react";
import IconButton from "@/components/IconButton";
import SegmentedButton from "@/components/SegmentedButton";
import type { MaterialNames } from "@/components/material_symbol_names";

export const RECORDING_PLAYBACK_RATES = [1, 1.25, 1.5, 2] as const;
export type RecordingPlaybackRate = (typeof RECORDING_PLAYBACK_RATES)[number];

export const RECORDING_PLAYBACK_RATE_STORAGE_KEY = "m360.call-recording.playback-rate";

// Playback preferences are intentionally global: one default per browser,
// shared across calls and tabs — standard media-player behavior.
const PLAYBACK_RATE_VALUES = new Set(RECORDING_PLAYBACK_RATES.map(String));

export function getStoredPlaybackRate(): RecordingPlaybackRate {
  try {
    const stored = window.localStorage.getItem(RECORDING_PLAYBACK_RATE_STORAGE_KEY);
    if (stored != null && PLAYBACK_RATE_VALUES.has(stored)) {
      return Number(stored) as RecordingPlaybackRate;
    }
  } catch {
    // Private browsing or unavailable storage — fall through to 1x.
  }
  return 1;
}

export const RECORDING_SKIP_SECONDS_OPTIONS = [5, 10, 30, 60] as const;
export type RecordingSkipSeconds = (typeof RECORDING_SKIP_SECONDS_OPTIONS)[number];

export const RECORDING_SKIP_SECONDS_STORAGE_KEY = "m360.call-recording.skip-seconds";

const SKIP_SECONDS_VALUES = new Set(RECORDING_SKIP_SECONDS_OPTIONS.map(String));

export function getStoredSkipSeconds(): RecordingSkipSeconds {
  try {
    const stored = window.localStorage.getItem(RECORDING_SKIP_SECONDS_STORAGE_KEY);
    if (stored != null && SKIP_SECONDS_VALUES.has(stored)) {
      return Number(stored) as RecordingSkipSeconds;
    }
  } catch {
    // Private browsing or unavailable storage — fall through to 10s.
  }
  return 10;
}

export function formatPlayerTime(totalSeconds: number | null | undefined): string {
  if (totalSeconds == null || !Number.isFinite(totalSeconds) || totalSeconds < 0) return "0:00";
  const floored = Math.floor(totalSeconds);
  const hours = Math.floor(floored / 3600);
  const minutes = Math.floor((floored % 3600) / 60);
  const seconds = floored % 60;
  if (hours === 0) return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
}

type Props = {
  audioUrl: string;
  callSid: string;
};

const SPEED_OPTIONS = RECORDING_PLAYBACK_RATES.map((rate) => ({
  label: `${rate}x`,
  value: String(rate)
}));

const SKIP_OPTIONS = RECORDING_SKIP_SECONDS_OPTIONS.map((seconds) => ({
  label: `${seconds}s`,
  value: String(seconds)
}));

// Numbered skip glyphs exist for 5/10/30 only — 60s falls back to the plain
// circular arrows. Aria labels always state the exact interval.
const SKIP_ICON_NAMES: Record<RecordingSkipSeconds, { back: MaterialNames; forward: MaterialNames }> = {
  5: { back: "replay_5", forward: "forward_5" },
  10: { back: "replay_10", forward: "forward_10" },
  30: { back: "replay_30", forward: "forward_30" },
  60: { back: "rotate_left", forward: "rotate_right" }
};

export default function RecordingPlayer({ audioUrl, callSid }: Props) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const rafRef = useRef<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState<number | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState<RecordingPlaybackRate>(() => getStoredPlaybackRate());
  const [skipSeconds, setSkipSeconds] = useState<RecordingSkipSeconds>(() => getStoredSkipSeconds());
  const [mediaError, setMediaError] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) audio.playbackRate = playbackRate;
    try {
      window.localStorage.setItem(RECORDING_PLAYBACK_RATE_STORAGE_KEY, String(playbackRate));
    } catch {
      // Ignore write failures (private browsing).
    }
  }, [playbackRate, audioUrl]);

  useEffect(() => {
    try {
      window.localStorage.setItem(RECORDING_SKIP_SECONDS_STORAGE_KEY, String(skipSeconds));
    } catch {
      // Ignore write failures (private browsing).
    }
  }, [skipSeconds]);

  const stopSmoothLoop = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  // `timeupdate` fires ~4Hz, which reads as a jumpy playhead. Drive the
  // slider from requestAnimationFrame while playing so it glides. Position
  // quantizes to the slider step (0.1s) so identical frames bail out of
  // re-rendering instead of churning state 60 times a second.
  const startSmoothLoop = useCallback(() => {
    stopSmoothLoop();
    const tick = () => {
      const audio = audioRef.current;
      if (audio && !audio.paused && !audio.ended) {
        const quantized = Math.floor(audio.currentTime * 10) / 10;
        setCurrentTime((previous) => (previous === quantized ? previous : quantized));
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [stopSmoothLoop]);

  useEffect(() => stopSmoothLoop, [stopSmoothLoop]);

  useEffect(() => {
    stopSmoothLoop();
    setCurrentTime(0);
    setDuration(null);
    setIsPlaying(false);
    setMediaError(false);
  }, [audioUrl, stopSmoothLoop]);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      if (duration != null && audio.currentTime >= duration - 0.1) {
        audio.currentTime = 0;
        setCurrentTime(0);
      }
      try {
        await audio.play();
      } catch (error) {
        // Interrupted play() (rapid toggle) aborts — pause event keeps state
        // correct. Any other rejection means the media cannot play.
        if (error instanceof DOMException && error.name === "AbortError") return;
        stopSmoothLoop();
        setIsPlaying(false);
        setMediaError(true);
      }
    } else {
      audio.pause();
    }
  }, [duration, stopSmoothLoop]);

  const handleSeek = useCallback((value: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = value;
    setCurrentTime(value);
  }, []);

  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = !audio.muted;
    setIsMuted(audio.muted);
  }, []);

  const handleSpeedChange = useCallback((value: string | null) => {
    if (value == null || !PLAYBACK_RATE_VALUES.has(value)) return;
    const match = RECORDING_PLAYBACK_RATES.find((rate) => rate === Number(value));
    if (match !== undefined) setPlaybackRate(match);
  }, []);

  const handleSkipChange = useCallback((value: string | null) => {
    if (value == null || !SKIP_SECONDS_VALUES.has(value)) return;
    const match = RECORDING_SKIP_SECONDS_OPTIONS.find((seconds) => seconds === Number(value));
    if (match !== undefined) setSkipSeconds(match);
  }, []);

  const skipBy = useCallback((deltaSeconds: number) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(audio.duration)) return;
    const next = Math.min(Math.max(0, audio.currentTime + deltaSeconds), audio.duration);
    audio.currentTime = next;
    setCurrentTime(next);
  }, []);

  const safeDuration = duration != null && Number.isFinite(duration) && duration > 0 ? duration : null;
  const progressPercent = safeDuration == null ? 0 : Math.min(100, Math.max(0, (currentTime / safeDuration) * 100));
  const skipIcons = SKIP_ICON_NAMES[skipSeconds];

  return (
    <div
      className="flex flex-col gap-3"
      role="group"
      aria-label="Call recording player"
      data-testid={`call-recording-controls-${callSid}`}
      onKeyDown={(event) => {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
        // Shift+arrows keep native fine stepping on the slider.
        if (event.shiftKey) return;
        // Plain arrows skip from the transport and seek slider, but never
        // from the Speed/Skip setting groups — those keep their own
        // interaction.
        if (!(event.target instanceof HTMLElement)) return;
        if (event.target.closest('[aria-label="Playback speed"], [aria-label="Skip interval"]')) return;
        event.preventDefault();
        skipBy(event.key === "ArrowLeft" ? -skipSeconds : skipSeconds);
      }}
    >
      <style>{`
        .m360-recording-seek {
          -webkit-appearance: none;
          appearance: none;
          height: 8px;
          border-radius: 9999px;
          cursor: pointer;
        }
        .m360-recording-seek:focus-visible {
          outline: 2px solid rgb(var(--color-sys-brand-primary));
          outline-offset: 4px;
        }
        .m360-recording-seek:disabled {
          opacity: 0.5;
          cursor: default;
        }
        .m360-recording-settings button:focus-visible {
          /* Inset ring: SegmentedButton sets inline outline:none, which a
          stylesheet outline can never beat — box-shadow still applies. */
          box-shadow: inset 0 0 0 2px rgb(var(--color-sys-brand-primary));
        }
        .m360-recording-seek::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 9999px;
          background: rgb(var(--color-sys-brand-surface));
          border: 3px solid rgb(var(--color-sys-brand-primary));
          box-shadow: 0 1px 3px rgb(0 0 0 / 0.3);
          cursor: pointer;
        }
        .m360-recording-seek::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border-radius: 9999px;
          background: rgb(var(--color-sys-brand-surface));
          border: 3px solid rgb(var(--color-sys-brand-primary));
          box-shadow: 0 1px 3px rgb(0 0 0 / 0.3);
          cursor: pointer;
        }
        .m360-recording-seek::-moz-range-track {
          height: 8px;
          border-radius: 9999px;
          background: transparent;
        }
      `}</style>
      {mediaError ? (
        <p
          className="text-body-medium text-sys-brand-on-surface-variant"
          data-testid={`call-recording-error-${callSid}`}
        >
          This recording can&apos;t be played.
        </p>
      ) : (
        <div className="flex items-center gap-2">
          <IconButton
            name={skipIcons.back}
            onClick={() => skipBy(-skipSeconds)}
            ariaLabel={`Back ${skipSeconds} seconds`}
            data-testid={`call-recording-back-${callSid}`}
            disabled={safeDuration == null}
            skipMargin
          />
          <IconButton
            name={isPlaying ? "pause" : "play_arrow"}
            onClick={togglePlay}
            ariaLabel={isPlaying ? "Pause recording" : "Play recording"}
            data-testid={`call-recording-play-${callSid}`}
            skipMargin
          />
          <IconButton
            name={skipIcons.forward}
            onClick={() => skipBy(skipSeconds)}
            ariaLabel={`Forward ${skipSeconds} seconds`}
            data-testid={`call-recording-forward-${callSid}`}
            disabled={safeDuration == null}
            skipMargin
          />
          <input
            type="range"
            min={0}
            max={safeDuration ?? 0}
            step={0.1}
            value={Math.min(currentTime, safeDuration ?? currentTime)}
            onChange={(event) => handleSeek(Number(event.target.value))}
            aria-label="Seek recording"
            aria-keyshortcuts="ArrowLeft ArrowRight"
            data-testid={`call-recording-seek-${callSid}`}
            className="m360-recording-seek min-w-0 flex-1"
            disabled={safeDuration == null}
            style={{
              background: `linear-gradient(to right, rgb(var(--color-sys-brand-primary)) 0%, rgb(var(--color-sys-brand-primary)) ${progressPercent}%, rgb(var(--color-sys-brand-surface-container-highest)) ${progressPercent}%, rgb(var(--color-sys-brand-surface-container-highest)) 100%)`
            }}
          />
          <span
            className="shrink-0 text-body-medium text-sys-brand-on-surface-variant tabular-nums"
            data-testid={`call-recording-time-${callSid}`}
          >
            {formatPlayerTime(currentTime)} / {safeDuration == null ? "--:--" : formatPlayerTime(safeDuration)}
          </span>
          <IconButton
            name={isMuted ? "volume_off" : "volume_up"}
            onClick={toggleMute}
            ariaLabel={isMuted ? "Unmute recording" : "Mute recording"}
            data-testid={`call-recording-mute-${callSid}`}
            skipMargin
          />
        </div>
      )}
      <div
        className="m360-recording-settings flex flex-wrap items-center gap-x-6 gap-y-3"
        data-testid={`call-recording-settings-${callSid}`}
      >
        <div className="flex items-center gap-3">
          <span className="text-body-medium text-sys-brand-on-surface-variant">Speed</span>
          <SegmentedButton
            options={SPEED_OPTIONS}
            value={String(playbackRate)}
            onChange={handleSpeedChange}
            ariaLabel="Playback speed"
          />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-body-medium text-sys-brand-on-surface-variant">Skip</span>
          <SegmentedButton
            options={SKIP_OPTIONS}
            value={String(skipSeconds)}
            onChange={handleSkipChange}
            ariaLabel="Skip interval"
          />
        </div>
      </div>
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="metadata"
        aria-hidden="true"
        data-testid="call-recording-player"
        onError={() => {
          stopSmoothLoop();
          setIsPlaying(false);
          setMediaError(true);
        }}
        onLoadedMetadata={(event) => {
          // Browsers may reset playbackRate when metadata loads; reassert.
          event.currentTarget.playbackRate = playbackRate;
          const value = event.currentTarget.duration;
          setDuration(Number.isFinite(value) ? value : null);
        }}
        onDurationChange={(event) => {
          const value = event.currentTarget.duration;
          setDuration(Number.isFinite(value) ? value : null);
        }}
        onPlay={(event) => {
          event.currentTarget.playbackRate = playbackRate;
          setIsPlaying(true);
          startSmoothLoop();
        }}
        onPause={() => {
          setIsPlaying(false);
          stopSmoothLoop();
        }}
        onEnded={(event) => {
          setIsPlaying(false);
          stopSmoothLoop();
          const finalTime = event.currentTarget.currentTime;
          setCurrentTime(Number.isFinite(finalTime) ? finalTime : 0);
        }}
      />
    </div>
  );
}
